/**
 * @file: mobile-layout/index.tsx
 *
 * The inbox mobile home surface: header chrome + the two-tab thread list
 * (the `_index` screen body on mobile).
 */

import { useChatUnreadCount } from '@euler/app/flows/chat/lib/session/hooks/useChatUnreadCount';
import { useFeedUnreadCount } from '@euler/app/flows/feed/lib/unread/hooks/useFeedUnreadCount';
import { useInboxTheme } from '@euler/app/flows/inbox/lib/listTheme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { DrawerActions } from '@react-navigation/native';
import { FC, memo, useCallback } from 'react';
import { View } from 'react-native';
import {
  useInboxNavigation,
  useOpenInboxSearch,
  useOpenInboxSettings,
} from '../../functions/useInboxNavigation';
import { InboxHeader } from '../InboxHeader';
import { ThreadTabs } from '../thread-tabs';

export const InboxMobileLayout: FC = memo(() => {
  const { safeAreaInsets } = useSystemMetrics();
  const { rpx } = useResponsiveContext();
  const theme = useInboxTheme();
  const navigation = useInboxNavigation();

  const feedUnread = useFeedUnreadCount();
  const chatUnread = useChatUnreadCount();

  const onMenuPress = useCallback(() => {
    navigation.dispatch(DrawerActions.openDrawer());
  }, [navigation]);

  const onSettingsPress = useOpenInboxSettings();
  const onSearchPress = useOpenInboxSearch();
  const onArchivePress = useCallback(() => {
    navigation.navigate('_archivedFeed', {});
  }, [navigation]);

  return (
    <View
      style={{
        flex: 1,
        paddingTop: safeAreaInsets.top + rpx(20),
        // No bottom inset: this screen is a bottom-tab scene, and the tab bar
        // below it already pads the home indicator. Adding it here leaves a
        // dead strip between the last row and the tab bar (invisible on web,
        // where the inset is 0).
        // Page wrapper background flows from the inbox theme — desktop
        // keeps the tinted gray, mobile is plain white. The header and
        // tab-bar inherit this since they're transparent.
        backgroundColor: theme.page,
      }}
    >
      <InboxHeader
        totalUnread={feedUnread + chatUnread}
        onMenuPress={onMenuPress}
        onSearchPress={onSearchPress}
        onArchivePress={onArchivePress}
        onMorePress={onSettingsPress}
      />
      <ThreadTabs />
    </View>
  );
});
